import getImagePath from '@/lib/get-image-path'

type ImageKitTransforms =
  | {
      height: number
      width: number
      [key: string]: string | number | null
    }
  | {}
  | null

/**
 * --------------------------------------------------------------------------
 * Generate Transform String
 *
 * @param {object} transforms - An object of ImageKit transforms
 * @returns {string} - The ImageKit transform string
 * --------------------------------------------------------------------------
 **/
function generateTransformString(transforms: ImageKitTransforms) {
  // Default ImageKit transforms
  const defaultTransforms = {
    dpr: 'auto',
    focus: 'auto',
    quality: '90',
    crop: 'maintain_ratio',
  }

  // Map of ImageKit transforms to their shorthand
  const keyMap = {
    dpr: 'dpr',
    focus: 'fo',
    height: 'h',
    width: 'w',
    crop: 'c', // crop strategy (maintain_ratio, force, at_max, etc.)
    cropMode: 'cm', // crop mode (pad_resize, pad, resize, extract, etc.)
    quality: 'q',
    format: 'f',
    progressive: 'pr',
    lossless: 'lo',
    trim: 't',
  } as { [key: string]: string }

  const combinedTransforms = { ...defaultTransforms, ...transforms }

  const transformString = Object.entries(combinedTransforms)
    .map((transform) => {
      const key = keyMap[transform[0]] || transform[0]
      const value = transform[1]

      return `${key}-${value}`
    })
    .join(',')

  return `?tr=${transformString}`
}

/**
 * --------------------------------------------------------------------------
 * Generate ImageKit URL
 *
 * @param {string} path - The image path (e.g., "/wp-content/uploads/2023/04/image.jpg")
 * @param {object} transform - An object of ImageKit transforms
 * @returns {string} - The fully constructed ImageKit URL
 * --------------------------------------------------------------------------
 */
function generateImageKitURL(path: string, transforms = {} as ImageKitTransforms) {
  const baseURL = process.env.NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT

  if (!path) {
    throw new Error('path is required')
  }

  // If the URL has already been transformed, skip
  if (path.includes('tr=')) {
    return path
  }

  // Format path correctly
  path = getImagePath(path)

  // Ensure the path does not have a leading slash to prevent double slashes
  const sanitizedPath = path.startsWith('/') ? path.slice(1) : path

  const transformString = generateTransformString(transforms)

  const url = `${baseURL}/${sanitizedPath}${transformString}`

  return url
}

export { generateImageKitURL, generateTransformString }
